import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../lib/api';
import { Briefcase, MapPin, Clock, ArrowRight, Send, CheckCircle, Users, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useSiteContent, useStaticTranslation } from '../context/SiteContentContext';
import { usePageTitle } from '../hooks/usePageTitle';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CareersPage() {
  const { language, t, localizedPath } = useSiteContent();
  const { st } = useStaticTranslation();
  
  // Set page title
  usePageTitle('careers');
  const [positions, setPositions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pageContent, setPageContent] = useState(null);
  const [applicationForm, setApplicationForm] = useState({
    name: '', email: '', phone: '', cover_letter: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [careersRes, contentRes] = await Promise.all([
          apiClient.get(`${API}/careers`),
          apiClient.get(`${API}/careers-page-content`)
        ]);
        setPositions(careersRes.data.positions);
        if (contentRes.data && Object.keys(contentRes.data).length > 0) {
          setPageContent(contentRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Helper to get localized content
  const getLocalized = (item, field) => {
    const localizedField = `${field}_${language}`;
    return item[localizedField] || item[`${field}_en`] || item[field] || '';
  };

  const getLocalizedArray = (item, field) => {
    const localizedField = `${field}_${language}`;
    const arr = item[localizedField] || item[`${field}_en`] || item[field] || [];
    return Array.isArray(arr) ? arr : [];
  };

  const handleApply = async (e) => {
    e.preventDefault();
    if (!selectedPosition) return;
    
    setIsSubmitting(true);
    try {
      const response = await apiClient.post(`${API}/careers/apply`, {
        ...applicationForm,
        position_id: selectedPosition.id
      });
      toast.success(response.data.message);
      setApplicationForm({ name: '', email: '', phone: '', cover_letter: '' });
      setSelectedPosition(null);
    } catch (error) {
      // 429 errors are handled by interceptor
      if (error.response?.status !== 429) {
        toast.error('Failed to submit application. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Default labels with trilingual support
  const labelsMap = {
    bg: {
      subtitle: 'КАРИЕРИ',
      title: 'Присъединете се към',
      titleHighlight: 'екип',
      description: 'Изградете кариерата си с водеща европейска транспортна компания.',
      whyWork: 'Защо да работите при нас',
      benefits: ['Конкурентни заплати', 'Здравно осигуряване', 'Професионално развитие', 'Модерен флот', 'Приятелска среда', 'Кариерно израстване'],
      openPositions: 'ОТВОРЕНИ ПОЗИЦИИ',
      currentOpportunities: 'Текущи възможности',
      noPositions: 'Няма отворени позиции в момента.',
      sendCV: 'Изпратете CV',
      applyNow: 'Кандидатствай',
      applyFor: 'Кандидатствай за',
      requirements: 'Изисквания',
      benefitsLabel: 'Предимства',
      fullName: 'Име и фамилия',
      email: 'Имейл',
      phone: 'Телефон',
      coverLetter: 'Мотивационно писмо',
      submit: 'Изпрати кандидатурата',
      submitting: 'Изпращане...',
      callUs: 'Обади се сега',
      ctaTitle: 'Не намирате подходяща позиция?',
      ctaDesc: 'Изпратете ни вашето CV!',
      ctaButton: 'Свържете се с HR'
    },
    en: {
      subtitle: 'CAREERS',
      title: 'Join Our',
      titleHighlight: 'Team',
      description: 'Build your career with a leading European transport company.',
      whyWork: 'Why Work With Us',
      benefits: ['Competitive salary', 'Health insurance', 'Professional development', 'Modern fleet', 'Friendly environment', 'Career growth'],
      openPositions: 'OPEN POSITIONS',
      currentOpportunities: 'Current Opportunities',
      noPositions: 'No open positions at the moment.',
      sendCV: 'Send Your CV',
      applyNow: 'Apply Now',
      applyFor: 'Apply for',
      requirements: 'Requirements',
      benefitsLabel: 'Benefits',
      fullName: 'Full Name',
      email: 'Email',
      phone: 'Phone',
      coverLetter: 'Cover Letter',
      submit: 'Submit Application',
      submitting: 'Submitting...',
      callUs: 'Call Us Now',
      ctaTitle: "Don't See a Matching Position?",
      ctaDesc: "Send us your CV!",
      ctaButton: 'Contact HR'
    },
    de: {
      subtitle: 'KARRIERE',
      title: 'Werden Sie Teil unseres',
      titleHighlight: 'Teams',
      description: 'Bauen Sie Ihre Karriere mit einem führenden europäischen Transportunternehmen auf.',
      whyWork: 'Warum bei uns arbeiten',
      benefits: ['Wettbewerbsfähiges Gehalt', 'Krankenversicherung', 'Berufliche Entwicklung', 'Moderne Flotte', 'Freundliche Umgebung', 'Karrierewachstum'],
      openPositions: 'OFFENE STELLEN',
      currentOpportunities: 'Aktuelle Möglichkeiten',
      noPositions: 'Derzeit keine offenen Stellen.',
      sendCV: 'Lebenslauf senden',
      applyNow: 'Jetzt bewerben',
      applyFor: 'Bewerben für',
      requirements: 'Anforderungen',
      benefitsLabel: 'Vorteile',
      fullName: 'Vollständiger Name',
      email: 'E-Mail',
      phone: 'Telefon',
      coverLetter: 'Anschreiben',
      submit: 'Bewerbung absenden',
      submitting: 'Wird gesendet...',
      callUs: 'Jetzt anrufen',
      ctaTitle: 'Keine passende Stelle gefunden?',
      ctaDesc: 'Senden Sie uns Ihren Lebenslauf!',
      ctaButton: 'HR kontaktieren'
    }
  };
  const defaultLabels = labelsMap[language] || labelsMap.en;

  // Helper to get localized page content
  const getLocalizedPageContent = (key, fallback) => {
    if (!pageContent) return fallback;
    return pageContent[`${key}_${language}`] || fallback;
  };

  // Build labels from pageContent or defaults
  const labels = pageContent ? {
    ...defaultLabels,
    subtitle: getLocalizedPageContent('hero_subtitle', defaultLabels.subtitle),
    title: getLocalizedPageContent('hero_title', defaultLabels.title),
    titleHighlight: getLocalizedPageContent('hero_title_highlight', defaultLabels.titleHighlight),
    description: getLocalizedPageContent('hero_description', defaultLabels.description),
    openPositions: getLocalizedPageContent('positions_subtitle', defaultLabels.openPositions),
    currentOpportunities: (getLocalizedPageContent('positions_title', '') + ' ' + getLocalizedPageContent('positions_title_highlight', '')).trim() || defaultLabels.currentOpportunities,
    noPositions: getLocalizedPageContent('no_positions', defaultLabels.noPositions),
    ctaTitle: getLocalizedPageContent('cta_title', defaultLabels.ctaTitle),
    ctaDesc: getLocalizedPageContent('cta_text', defaultLabels.ctaDesc),
    ctaButton: getLocalizedPageContent('cta_button', defaultLabels.ctaButton)
  } : defaultLabels;

  // Show loader while data is loading
  if (isLoading) {
    return (
      <div className="page-loader">
        <div className="page-loader-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="careers-page" className="pt-20 bg-slate-50">
      <SEO page="careers" />
      {/* Hero */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <span className="overline mb-4 block">{labels.subtitle}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6">
            {labels.title} <span className="text-[#C41E3A]">{labels.titleHighlight}</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl">{labels.description}</p>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-16 bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="overline mb-4 block">TETRALOG</span>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
              {labels.whyWork.split(' ').slice(0, -2).join(' ')} <span className="text-[#C41E3A]">{labels.whyWork.split(' ').slice(-2).join(' ')}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {labels.benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-white rounded-2xl shadow-sm">
                <CheckCircle className="w-5 h-5 text-[#C41E3A] flex-shrink-0" />
                <span className="text-slate-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section data-testid="positions-section" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="overline mb-4 block">{labels.openPositions}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
              {labels.currentOpportunities.split(' ')[0]} <span className="text-[#C41E3A]">{labels.currentOpportunities.split(' ').slice(1).join(' ')}</span>
            </h2>
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="spinner mx-auto"></div>
            </div>
          ) : positions.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-3xl">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">{labels.noPositions}</h3>
              <p className="text-slate-500 mb-6">{labels.noPositionsDesc}</p>
              <Link to={localizedPath('/contact')}>
                <Button className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full">
                  Send Your CV Anyway
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {positions.map((position, index) => (
                <Card 
                  key={position.id}
                  data-testid={`position-card-${position.id}`}
                  className="bg-slate-50 border-slate-100 hover:shadow-lg transition-all rounded-3xl animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardContent className="p-6 md:p-8">
                    {/* Header with title and apply button */}
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Briefcase className="w-5 h-5 text-[#C41E3A]" />
                          <h3 className="text-2xl font-bold text-slate-900">{getLocalized(position, 'title')}</h3>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" /> {getLocalized(position, 'location') || position.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" /> {getLocalized(position, 'type') || position.type}
                          </span>
                          {(getLocalized(position, 'department') || position.department) && (
                            <span className="px-3 py-1 bg-red-50 text-[#C41E3A] rounded-full text-xs font-medium">
                              {getLocalized(position, 'department') || position.department}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            onClick={() => setSelectedPosition(position)}
                            className="bg-[#C41E3A] text-white hover:bg-[#A01830] rounded-full"
                          >
                            {labels.applyNow} <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-3xl" onOpenAutoFocus={(e) => e.preventDefault()}>
                          <DialogHeader>
                            <DialogTitle className="text-2xl font-bold text-slate-900">
                              {labels.applyFor} {getLocalized(position, 'title')}
                            </DialogTitle>
                          </DialogHeader>
                          
                          <div className="mt-4 space-y-6">
                            {/* Position Details */}
                            <div className="p-4 bg-slate-50 rounded-2xl">
                              <h4 className="font-bold text-slate-900 mb-2">{labels.requirements}</h4>
                              <ul className="space-y-1">
                                {getLocalizedArray(position, 'requirements').map((req, i) => (
                                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                                    <CheckCircle className="w-4 h-4 text-[#C41E3A] mt-0.5 flex-shrink-0" />
                                    {req}
                                  </li>
                                ))}
                              </ul>
                            </div>

                            {/* Application Form */}
                            <form onSubmit={handleApply} className="space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <Label className="text-slate-600">{labels.fullName} *</Label>
                                  <Input
                                    required
                                    value={applicationForm.name}
                                    onChange={(e) => setApplicationForm({...applicationForm, name: e.target.value})}
                                    className="mt-1 bg-slate-50 border-slate-200 rounded-xl text-base"
                                  />
                                </div>
                                <div>
                                  <Label className="text-slate-600">{labels.email} *</Label>
                                  <Input
                                    type="email"
                                    required
                                    value={applicationForm.email}
                                    onChange={(e) => setApplicationForm({...applicationForm, email: e.target.value})}
                                    className="mt-1 bg-slate-50 border-slate-200 rounded-xl text-base"
                                  />
                                </div>
                              </div>
                              <div>
                                <Label className="text-slate-600">{labels.phone} *</Label>
                                <Input
                                  required
                                  value={applicationForm.phone}
                                  onChange={(e) => setApplicationForm({...applicationForm, phone: e.target.value})}
                                  className="mt-1 bg-slate-50 border-slate-200 rounded-xl text-base"
                                  placeholder="+359 XXX XXX XXX"
                                />
                              </div>
                              <div>
                                <Label className="text-slate-600">{labels.coverLetter}</Label>
                                <Textarea
                                  value={applicationForm.cover_letter}
                                  onChange={(e) => setApplicationForm({...applicationForm, cover_letter: e.target.value})}
                                  className="mt-1 bg-slate-50 border-slate-200 rounded-xl min-h-[120px] text-base"
                                />
                              </div>
                              <div className="space-y-3">
                                <Button 
                                  type="submit"
                                  disabled={isSubmitting}
                                  className="w-full bg-[#C41E3A] text-white hover:bg-[#A01830] font-bold uppercase tracking-wide rounded-full py-6"
                                >
                                  {isSubmitting ? labels.submitting : <><Send className="w-4 h-4 mr-2" /> {labels.submit}</>}
                                </Button>
                                {position.contact_phone && (
                                  <a href={`tel:${position.contact_phone}`} className="block">
                                    <Button 
                                      type="button"
                                      variant="outline"
                                      className="w-full border-2 border-[#C41E3A] text-[#C41E3A] hover:bg-[#C41E3A] hover:text-white font-bold uppercase tracking-wide rounded-full py-6"
                                    >
                                      <Phone className="w-4 h-4 mr-2" /> {labels.callUs}
                                    </Button>
                                  </a>
                                )}
                              </div>
                            </form>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>

                    {/* Full Description - Always Visible */}
                    <div className="mb-6">
                      <p className="text-slate-600 text-base leading-relaxed">{getLocalized(position, 'description')}</p>
                    </div>

                    {/* Requirements & Benefits - Always Visible */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Requirements */}
                      {getLocalizedArray(position, 'requirements').length > 0 && (
                        <div className="p-4 bg-white rounded-2xl border border-slate-100">
                          <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-[#C41E3A]" />
                            {labels.requirements}
                          </h4>
                          <ul className="space-y-2">
                            {getLocalizedArray(position, 'requirements').map((req, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                                <span className="text-[#C41E3A] mt-0.5">•</span>
                                {req}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Benefits */}
                      {getLocalizedArray(position, 'benefits').length > 0 && (
                        <div className="p-4 bg-white rounded-2xl border border-slate-100">
                          <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                            {labels.benefitsLabel}
                          </h4>
                          <ul className="space-y-2">
                            {getLocalizedArray(position, 'benefits').map((benefit, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                                <span className="text-green-500 mt-0.5">•</span>
                                {benefit}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#C41E3A] to-[#A01830]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {labels.ctaTitle}
          </h2>
          <p className="text-white/80 mb-8">
            {labels.ctaDesc}
          </p>
          <Link to={localizedPath('/contact')}>
            <Button className="bg-white text-[#C41E3A] hover:bg-slate-100 font-bold uppercase tracking-wide px-8 py-6 rounded-full">
              {labels.ctaButton} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
